import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import {
  SidePanel,
  SidePanelLoader,
  SidePanelType,
  Popup,
  PermissionDenied,
  ResponseError,
  responseErrorsResolver,
  ajvResolver,
  ButtonPrimary,
  ButtonSecondary,
  usePrevious,
  Tabs,
  ChangeLogs,
  PermissionEnum,
} from '@ui/src';
import { useAppSelector, useAppDispatch } from '@store';
import {
  CurrencyTypeEnum,
  ProcessingRule,
  ProcessingRulesFormValues,
  ProcessingTypeEnum,
  PspParterAccountSelectTypeEnum,
} from './types';
import {
  useLazyGetProcessingRuleQuery,
  useAddProcessingRuleMutation,
  useUpdateProcessingRuleMutation,
  useLazyGetProcessingRulesQuery,
  useLazyGetProcessingRulesChangeLogsQuery,
  useLazyGetPspAccountByIdQuery,
} from './store/service';
import { clearProcessingRuleState } from './store/slice';
import ProcessingRuleFormContent from '../../../components/settings/processing-rules/form';
import { useLazyGetEmployeesCASPQuery } from '../../settings/store/services/transactions-types';
import useGetPrimaryAccountsAutocompleteList from './hooks/use-get-psp-partner-accounts';

import ROUTES from '../../../routes';

import cn from '../../settings/styles.module.css';

const schema = {
  type: 'object',
  properties: {
    active: {
      type: 'string',
      minLength: 1,
      errorMessage: 'Rule status is required',
    },
    typeId: {
      type: 'string',
      minLength: 1,
      errorMessage: 'Transaction Type is required',
    },
    fromStatus: {
      type: 'string',
      minLength: 1,
      errorMessage: 'From Status is required',
    },
    toStatus: {
      type: 'string',
      minLength: 1,
      errorMessage: 'To Status is required',
    },
    processingType: {
      type: 'string',
      minLength: 1,
      errorMessage: 'Processing Type is required',
    },
    pspIntegrationType: {
      type: 'string',
      minLength: 1,
      errorMessage: 'Bank Integration Type is required',
    },
  },
  required: [
    'active',
    'typeId',
    'fromStatus',
    'toStatus',
    'processingType',
    'pspIntegrationType',
  ],
};

const ProcessingRuleForm = () => {
  const [panelIsOpen, setPanelIsOpen] = useState(false);
  const [dirtyPopupVisible, setDirtyPopupVisible] = useState(false);
  const [globalError, setGlobalError] = useState('');
  const [formattedSchema, setFormattedSchema] = useState(schema);
  const [activeTab, setActiveTab] = useState<'general' | 'logs'>('general');

  const { ruleId } = useParams();

  const { search } = useLocation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const {
    administrationDictionaries: { activePermissions, activeCurrencies, users },
    settings: {
      paymentTypes: { paymentTypes },
      transactionsTypes: { casp, values: transactionsTypes },
      processingRules: {
        takeCount,
        processingRule,
        pspIntegrations,
        pspPartnes,
        changeLogs,
        changeLogsPagination,
      },
    },
    transactionsDictionaries: { overdraftEnabled },
  } = useAppSelector((state) => state);

  const [getProcessingRule, { isLoading, isFetching }] =
    useLazyGetProcessingRuleQuery();
  const [getProcessingRules] = useLazyGetProcessingRulesQuery();
  const [addRule, { isLoading: addRuleInProgress, isSuccess }] =
    useAddProcessingRuleMutation();
  const [
    updateRule,
    { isLoading: updateRuleInProgress, isSuccess: updateIsSuccess },
  ] = useUpdateProcessingRuleMutation();
  const [
    getChangeLogs,
    { isLoading: changeLogsIsLoading, isFetching: changeLogsIsFetching },
  ] = useLazyGetProcessingRulesChangeLogsQuery();
  const [getEmployeesCasp] = useLazyGetEmployeesCASPQuery();
  const [getPspAccountById] = useLazyGetPspAccountByIdQuery();

  const {
    control,
    formState: { errors, isDirty },
    handleSubmit,
    setError,
    reset,
    watch,
    clearErrors,
    setValue,
  } = useForm<ProcessingRulesFormValues>({
    resolver: ajvResolver(formattedSchema),
  });

  const currencyType = watch('currencyType');
  const pspIntegrationType = watch('pspIntegrationType');
  const pspPartnerType = watch('pspPartnerType');
  const pspPartnerId = watch('pspPartnerId');
  const currencyId = watch('currencyId');
  const transactionType = watch('typeId');
  const paymentCategoryId = watch('paymentCategoryId');
  const caspId = watch('caspId');
  const transactionCurrenciesIds = watch('currenciesIds');
  const timeSchedule = watch('timeWindowType');
  const pspParterAccountSelectType = watch('pspPartnerAccountSelectType');
  const processingType = watch('processingType');
  const pspPartnerAccountId = watch('pspPartnerAccountId');

  const prevCurrencyType = usePrevious(currencyType);
  const prevPspIntegrationType = usePrevious(pspIntegrationType);
  const prevPspPartnerType = usePrevious(pspPartnerType);
  const prevPaymentCategoryId = usePrevious(paymentCategoryId);
  const prevPspPartnerId = usePrevious(pspPartnerId);
  const prevTransactionType = usePrevious(transactionType);
  const prevPspParterAccountSelectType = usePrevious(
    pspParterAccountSelectType,
  );
  const prevProcessingType = usePrevious(processingType);
  const prevTimeSchedule = usePrevious(timeSchedule);

  const getChangeLogsAction = useCallback(
    (cursor?: string) => {
      getChangeLogs(
        `?take=20&entityType=TRANSACTION_RULE&entityId=${ruleId}${
          cursor ? `&cursor=${cursor}` : ''
        }`,
      );
    },
    [ruleId],
  );

  const {
    autocompleteList: pspPartnerAccounts,
    onSearch: onSearchPspPartnerAccounts,
    isLoading: pspPartnerAccountsIsLoading,
    setAutocompleteList,
    setAccountsList,
    setInitialList,
    getAutocompleteListAction,
    autocompleteListPagination,
    nextPageIsLoading,
    onScrollAutocompleteListAction,
  } = useGetPrimaryAccountsAutocompleteList(
    pspPartnerId as string,
    transactionCurrenciesIds,
    ruleId,
  );

  const getEmployeesCaspAction = useCallback(async () => {
    await getEmployeesCasp();
  }, []);

  const getPspAccountByIdAction = useCallback(
    async (id: string, partnerId: string, currenciesIdsList?: string[]) => {
      const response = await getPspAccountById(id);

      if (response?.data?.data) {
        const account = response?.data?.data;

        if (account) {
          setAutocompleteList([
            {
              name: account.description || '',
              description: '',
              value: response?.data?.data.id,
              suffix: `${response?.data?.data?.account?.fullNumber} ${
                // eslint-disable-next-line no-nested-ternary
                overdraftEnabled
                  ? response?.data?.data?.account?.overdraftEnabled
                    ? '(Overdraft)'
                    : ''
                  : ''
              }`,
            },
          ]);
          setInitialList([
            {
              name: account.description || '',
              description: '',
              value: response?.data?.data.id,
              suffix: `${response?.data?.data?.account?.fullNumber} ${
                // eslint-disable-next-line no-nested-ternary
                overdraftEnabled
                  ? response?.data?.data?.account?.overdraftEnabled
                    ? '(Overdraft)'
                    : ''
                  : ''
              }`,
            },
          ]);
          setAccountsList([
            {
              ...account,
            },
          ]);
        }
      } else if (response?.error) {
        getAutocompleteListAction(partnerId, currenciesIdsList);
      }
    },
    [],
  );

  const handleClose = () => {
    setGlobalError('');
    setPanelIsOpen(false);

    if (ruleId) {
      dispatch(clearProcessingRuleState());
    }

    setTimeout(() => {
      navigate(
        `${ROUTES.TRANSACTION_SETTINGS_PROCESSING_RULES_LIST}${search || ''}`,
      );
    }, 100);
  };

  const onClearPspAccount = () => {
    getAutocompleteListAction();
  };

  const onSubmit = handleSubmit(async (values) => {
    setGlobalError('');

    const body: ProcessingRulesFormValues = {
      active: Boolean(values.active === 'true'),
      typeId: values.typeId,
      fromStatus: values.fromStatus,
      toStatus: values.toStatus,
      processingType: values.processingType,
      currencyType: values.currencyType,
      pspIntegrationType: values.pspIntegrationType,
      pspPartnerType: values.pspPartnerType,
    };

    if (values.currencyId) {
      body.currencyId = values.currencyId;
    }

    if (values.amount) {
      body.amount = Number(values.amount);
    }

    if (values.pspIntegrationId) {
      body.pspIntegrationId = values.pspIntegrationId;
    }

    if (values.pspPartnerId) {
      body.pspPartnerId = values.pspPartnerId;
    }

    if (values?.currenciesIds && values.currenciesIds?.length) {
      body.currenciesIds = values.currenciesIds;
    } else {
      body.currenciesIds = [];
    }

    if (values?.paymentCategoryId) {
      body.paymentCategoryId = values.paymentCategoryId;
    }

    if (values.caspId) {
      body.caspId = values.caspId;
    }

    if (values.timeWindowType) {
      body.timeWindowType = values.timeWindowType;
    }

    if (values.timeWindowStart) {
      body.timeWindowStart = values.timeWindowStart;
    }

    if (values.timeWindowEnd) {
      body.timeWindowEnd = values.timeWindowEnd;
    }

    if (values.pspPartnerAccountSelectType) {
      body.pspPartnerAccountSelectType = values.pspPartnerAccountSelectType;
    }

    if (values.pspPartnerAccountId) {
      body.pspPartnerAccountId = values.pspPartnerAccountId;
    }

    let response;

    if (ruleId) {
      if (processingRule?.currencyId && !values.currencyId) {
        body.currencyId = null;
      }

      if (processingRule?.pspIntegrationId && !values.pspIntegrationId) {
        body.pspIntegrationId = null;
      }

      if (processingRule?.pspPartnerId && !values.pspPartnerId) {
        body.pspPartnerId = null;
      }

      if (processingRule?.currenciesIds && !values?.currenciesIds?.length) {
        body.currenciesIds = [];
      }

      if (processingRule?.timeWindowStart && !values.timeWindowStart) {
        body.timeWindowStart = null;
      }

      if (processingRule?.timeWindowEnd && !values.timeWindowEnd) {
        body.timeWindowEnd = null;
      }

      if (processingRule?.paymentCategoryId && !values.paymentCategoryId) {
        body.paymentCategoryId = null;
      }

      if (processingRule?.caspId && !values.caspId) {
        body.caspId = null;
      }

      if (
        processingRule?.pspPartnerAccountSelectType &&
        !values.pspPartnerAccountSelectType
      ) {
        body.pspPartnerAccountSelectType = null;
      }

      if (processingRule?.pspPartnerAccountId && !values.pspPartnerAccountId) {
        body.pspPartnerAccountId = null;
      }

      response = (await updateRule({
        id: ruleId,
        body,
      })) as { error?: ResponseError; data: { data: ProcessingRule } };
    } else {
      response = (await addRule(body)) as {
        error?: ResponseError;
        data: { data: ProcessingRule };
      };
    }

    if (response?.error) {
      responseErrorsResolver({
        errors: response.error,
        setError,
        setGlobalError,
      });
    } else if (!response.error && ruleId) {
      const { data } = response.data;

      reset({
        active: data.active ? 'true' : 'false',
        typeId: data.typeId,
        fromStatus: data.fromStatus,
        toStatus: data.toStatus,
        processingType: data.processingType,
        currencyType: data.currencyType,
        currencyId: data?.currencyId || '',
        amount: data?.amount ? data.amount.toString() : '',
        pspIntegrationType: data.pspIntegrationType,
        pspIntegrationId: data?.pspIntegrationId || '',
        pspPartnerType: data.pspIntegrationType,
        pspPartnerId: data?.pspPartnerId || '',
        paymentCategoryId: processingRule?.paymentCategoryId || '',
        currenciesIds: processingRule?.currenciesIds || [],
        caspId: processingRule?.caspId || '',
        pspPartnerAccountId: processingRule?.pspPartnerAccountId || '',
        pspPartnerAccountSelectType:
          processingRule?.pspPartnerAccountSelectType || '',
        timeWindowType: processingRule?.timeWindowType || '',
        timeWindowStart: processingRule?.timeWindowStart || '',
        timeWindowEnd: processingRule?.timeWindowEnd || '',
      });
    }
  });

  useEffect(() => {
    if (
      paymentCategoryId &&
      prevPaymentCategoryId &&
      paymentCategoryId !== prevPaymentCategoryId
    ) {
      setValue('caspId', '');
      setValue('currenciesIds', []);
    }
  }, [paymentCategoryId, prevPaymentCategoryId]);

  useEffect(() => {
    if (
      (pspParterAccountSelectType &&
        prevPspParterAccountSelectType &&
        pspParterAccountSelectType !==
          PspParterAccountSelectTypeEnum.SPECIFIC &&
        prevPspParterAccountSelectType ===
          PspParterAccountSelectTypeEnum.SPECIFIC) ||
      (prevPspPartnerId && pspPartnerId && prevPspPartnerId !== pspPartnerId)
    ) {
      setValue('pspPartnerAccountId', '');
    }
  }, [
    prevPspParterAccountSelectType,
    pspParterAccountSelectType,
    pspPartnerId,
    prevPspPartnerId,
  ]);

  useEffect(() => {
    if (timeSchedule && prevTimeSchedule && prevTimeSchedule !== timeSchedule) {
      setValue('timeWindowStart', '');
      setValue('timeWindowEnd', '');
    }
  }, [timeSchedule, prevTimeSchedule]);

  useEffect(() => {
    if (
      (prevTransactionType &&
        transactionType &&
        prevTransactionType ===
          transactionsTypes?.find((item) => item.name === 'OUTGOING')?.id &&
        transactionType !==
          transactionsTypes?.find((item) => item.name === 'OUTGOING')?.id) ||
      (processingType &&
        prevProcessingType &&
        prevProcessingType !== processingType)
    ) {
      setValue('amount', '');
      setValue('currencyId', '');
      setValue('paymentCategoryId', '');
      setValue('currenciesIds', []);
      setValue('timeWindowType', '');
      setValue('timeWindowStart', '');
      setValue('timeWindowEnd', '');
      setValue('pspPartnerAccountSelectType', '');
      setValue('pspPartnerId', '');
      setValue('pspPartnerAccountId', '');
      setValue('pspPartnerType', '');
      setValue('currencyType', '');
      setValue('processingType', '');
    }
  }, [
    transactionType,
    prevTransactionType,
    processingType,
    prevProcessingType,
    transactionsTypes,
  ]);

  useEffect(() => {
    if (
      processingType &&
      transactionType &&
      processingType === ProcessingTypeEnum.PSP_AUTO_SELECT &&
      transactionType ===
        transactionsTypes?.find((item) => item.name === 'OUTGOING')?.id &&
      !pspPartnerType
    ) {
      setValue('pspPartnerType', CurrencyTypeEnum.SPECIFIC);
    }
  }, [processingType, transactionType, pspPartnerType, transactionType]);

  useEffect(() => {
    if (
      currencyType !== CurrencyTypeEnum.SPECIFIC &&
      transactionCurrenciesIds?.length
    ) {
      setValue('currenciesIds', []);
    }
  }, [currencyType, transactionCurrenciesIds]);

  useEffect(() => {
    setPanelIsOpen(true);

    if (!casp) {
      getEmployeesCaspAction();
    }

    if (
      ruleId &&
      activePermissions?.includes(PermissionEnum.TRANSACTIONS_RULES_LIST)
    ) {
      getProcessingRule(ruleId);
    }
  }, [ruleId, activePermissions, casp]);

  useEffect(() => {
    if (processingRule && processingRule?.pspPartnerAccountId) {
      getPspAccountByIdAction(
        processingRule?.pspPartnerAccountId,
        processingRule?.pspPartnerId as string,
        processingRule?.currenciesIds,
      );
    }
  }, [processingRule]);

  useEffect(() => {
    let nextSchema: any = formattedSchema;

    if (currencyType === CurrencyTypeEnum.SPECIFIC) {
      nextSchema = {
        ...nextSchema,
        properties: {
          ...nextSchema.properties,
          currencyId: {
            type: 'string',
            minLength: 1,
            errorMessage: 'Currency is required',
          },
        },
      };
    } else if (
      currencyType !== CurrencyTypeEnum.SPECIFIC &&
      prevCurrencyType &&
      prevCurrencyType === CurrencyTypeEnum.SPECIFIC
    ) {
      clearErrors(['currencyId']);

      delete nextSchema.properties.currencyId;

      nextSchema = {
        ...nextSchema,
        properties: {
          ...nextSchema.properties,
        },
      };
    }

    if (pspIntegrationType === CurrencyTypeEnum.SPECIFIC) {
      nextSchema = {
        ...nextSchema,
        properties: {
          ...nextSchema.properties,
          pspIntegrationId: {
            type: 'string',
            minLength: 1,
            errorMessage: 'Integration is required',
          },
        },
      };
    } else if (
      pspIntegrationType !== CurrencyTypeEnum.SPECIFIC &&
      prevPspIntegrationType &&
      prevPspIntegrationType === CurrencyTypeEnum.SPECIFIC
    ) {
      clearErrors(['pspIntegrationId']);

      delete nextSchema.properties.pspIntegrationId;

      nextSchema = {
        ...nextSchema,
        properties: {
          ...nextSchema.properties,
        },
      };
    }

    if (pspPartnerType === CurrencyTypeEnum.SPECIFIC) {
      nextSchema = {
        ...nextSchema,
        properties: {
          ...nextSchema.properties,
          pspPartnerId: {
            type: 'string',
            minLength: 1,
            errorMessage: 'Bank is required',
          },
        },
      };
    } else if (
      pspPartnerType !== CurrencyTypeEnum.SPECIFIC &&
      prevPspPartnerType &&
      prevPspPartnerType === CurrencyTypeEnum.SPECIFIC
    ) {
      clearErrors(['pspPartnerId']);

      delete nextSchema.properties.pspPartnerId;

      nextSchema = {
        ...nextSchema,
        properties: {
          ...nextSchema.properties,
        },
      };
    }

    setFormattedSchema(nextSchema);
  }, [
    pspIntegrationType,
    pspPartnerType,
    currencyType,
    prevCurrencyType,
    prevPspPartnerType,
    prevPspIntegrationType,
  ]);

  useEffect(() => {
    if (processingRule) {
      reset({
        active: processingRule.active ? 'true' : 'false',
        typeId: processingRule.typeId,
        fromStatus: processingRule.fromStatus,
        toStatus: processingRule.toStatus,
        processingType: processingRule.processingType,
        currencyType: processingRule.currencyType,
        currencyId: processingRule?.currencyId || '',
        amount: processingRule?.amount ? processingRule.amount.toString() : '',
        pspIntegrationType: processingRule.pspIntegrationType,
        pspIntegrationId: processingRule?.pspIntegrationId || '',
        pspPartnerType: processingRule.pspIntegrationType,
        pspPartnerId: processingRule?.pspPartnerId || '',
        paymentCategoryId: processingRule?.paymentCategoryId || '',
        currenciesIds: processingRule?.currenciesIds || [],
        caspId: processingRule?.caspId || '',
        pspPartnerAccountId: processingRule?.pspPartnerAccountId || '',
        pspPartnerAccountSelectType:
          processingRule?.pspPartnerAccountSelectType || '',
        timeWindowType: processingRule?.timeWindowType || '',
        timeWindowStart: processingRule?.timeWindowStart || '',
        timeWindowEnd: processingRule?.timeWindowEnd || '',
      });
    }
  }, [processingRule]);

  useEffect(() => {
    if (isSuccess || updateIsSuccess) {
      if (isSuccess) {
        getProcessingRules(`${search}&take=${takeCount}`);
      }
      handleClose();
    }
  }, [isSuccess, search, updateIsSuccess]);

  useEffect(() => {
    if (
      activeTab === 'logs' &&
      activePermissions?.includes(PermissionEnum.AUDIT_LOG_LIST)
    ) {
      getChangeLogsAction();
    }
  }, [activeTab]);

  let content = <div />;

  if (isLoading || isFetching) {
    content = <SidePanelLoader />;
  } else if (!isLoading && !isFetching) {
    if (
      !ruleId &&
      !activePermissions?.includes(
        PermissionEnum.TRANSACTIONS_RULES_CREATE_ACTION,
      )
    ) {
      content = <PermissionDenied />;
    } else {
      content = (
        <>
          {ruleId &&
            activePermissions?.includes(PermissionEnum.AUDIT_LOG_LIST) && (
              <Tabs
                wrapperClassName={cn.processingRulesTabs}
                activeTab={activeTab}
                tabs={[
                  {
                    name: 'Details',
                    value: 'general',
                  },
                  {
                    name: 'Change Log',
                    value: 'logs',
                  },
                ]}
                onClick={(tab) => setActiveTab(tab as 'general' | 'logs')}
              />
            )}
          {activeTab === 'general' && (
            <ProcessingRuleFormContent
              control={control}
              errors={errors}
              pspIntegrations={pspIntegrations}
              pspPartners={pspPartnes}
              currencies={activeCurrencies}
              currencyType={currencyType}
              pspIntegrationType={pspIntegrationType}
              pspPartnerType={pspPartnerType}
              setValue={setValue}
              currencyId={currencyId as string}
              transactionType={transactionType}
              paymentCategoryId={paymentCategoryId}
              paymentTypes={paymentTypes}
              caspId={caspId}
              ruleId={ruleId}
              casp={casp}
              transactionCurrenciesIds={transactionCurrenciesIds}
              timeSchedule={timeSchedule}
              pspParterAccountSelectType={pspParterAccountSelectType}
              pspPartnerAccounts={pspPartnerAccounts}
              pspPartnerId={pspPartnerId as string | undefined}
              onSearchPspPartnerAccounts={onSearchPspPartnerAccounts}
              pspPartnerAccountsIsLoading={pspPartnerAccountsIsLoading}
              processingType={processingType}
              onClearPspAccount={onClearPspAccount}
              getAutocompleteListAction={onScrollAutocompleteListAction}
              autocompleteListPagination={autocompleteListPagination}
              nextPageIsLoading={nextPageIsLoading}
              transactionTypes={transactionsTypes}
              pspPartnerAccountId={pspPartnerAccountId}
            />
          )}
          {activeTab === 'logs' && (
            <ChangeLogs
              entityType="TRANSACTION_RULE"
              entityId={ruleId as string}
              changeLogsIsLoading={changeLogsIsLoading}
              changeLogs={changeLogs}
              activePermission={activePermissions}
              users={users}
              pagination={changeLogsPagination}
              changeLogsIsFetching={changeLogsIsFetching}
              getChangeLogs={getChangeLogsAction}
            />
          )}
        </>
      );
    }
  }

  let buttonText = '';

  if (addRuleInProgress || updateRuleInProgress) {
    buttonText = 'Loading...';
  } else if (ruleId) {
    buttonText = 'Save rule';
  } else if (!ruleId) {
    buttonText = 'Add rule';
  }

  return (
    <>
      <SidePanel
        isOpen={panelIsOpen}
        type={SidePanelType.Customers}
        onClose={() => {
          if (isDirty) {
            setDirtyPopupVisible(true);
          } else {
            handleClose();
          }
        }}
        title={ruleId ? 'Edit Processing Rule' : 'Add Processing Rule'}
        errorSidepanel={globalError}
        sidepanelExpandedKey="processing-rule-panel"
        footer={
          <>
            <ButtonPrimary
              text={buttonText}
              disabled={
                (!ruleId && !isDirty) ||
                addRuleInProgress ||
                updateRuleInProgress
              }
              onClick={onSubmit}
              bold
            />
            <ButtonSecondary
              text="Close"
              className="button-white"
              onClick={() => {
                if (isDirty) {
                  setDirtyPopupVisible(true);
                } else {
                  handleClose();
                }
              }}
              bold
            />
          </>
        }
        closeOnClickOutside
      >
        {content}
      </SidePanel>
      <Popup
        title="You have unsaved changes"
        description=""
        onCancelClick={() => setDirtyPopupVisible(false)}
        onOkClick={() => {
          setDirtyPopupVisible(false);
          handleClose();
        }}
        cancelButtonText="Cancel"
        okButtonText="Close"
        isVisible={dirtyPopupVisible}
        parent={document.getElementById('dirty-popup') as HTMLElement}
      />
      <div id="dirty-popup" />
    </>
  );
};

export default ProcessingRuleForm;
